/**
 * TagBadge - Displays a colored tag badge for posts
 * Supports predefined colors with special styling for urgent tags
 */

import React from 'react';
import { AlertCircle, Newspaper, MessageSquare, HelpCircle, Heart, Search, Stethoscope, GraduationCap, Smile, Camera } from 'lucide-react';

// Tag color and icon mapping
const TAG_CONFIG = {
  urgent: { 
    color: 'bg-red-500 text-white', 
    hoverColor: 'hover:bg-red-600',
    icon: AlertCircle,
    pulse: true
  },
  news: { 
    color: 'bg-blue-500 text-white', 
    hoverColor: 'hover:bg-blue-600',
    icon: Newspaper 
  },
  discussion: { 
    color: 'bg-green-500 text-white', 
    hoverColor: 'hover:bg-green-600',
    icon: MessageSquare 
  },
  help: { 
    color: 'bg-orange-500 text-white', 
    hoverColor: 'hover:bg-orange-600',
    icon: HelpCircle 
  },
  adoption: { 
    color: 'bg-purple-500 text-white', 
    hoverColor: 'hover:bg-purple-600',
    icon: Heart 
  },
  'lost-found': { 
    color: 'bg-amber-500 text-white', 
    hoverColor: 'hover:bg-amber-600',
    icon: Search 
  },
  health: { 
    color: 'bg-teal-500 text-white', 
    hoverColor: 'hover:bg-teal-600',
    icon: Stethoscope 
  },
  training: { 
    color: 'bg-indigo-500 text-white', 
    hoverColor: 'hover:bg-indigo-600',
    icon: GraduationCap 
  },
  funny: { 
    color: 'bg-pink-500 text-white', 
    hoverColor: 'hover:bg-pink-600',
    icon: Smile 
  },
  photo: { 
    color: 'bg-cyan-500 text-white', 
    hoverColor: 'hover:bg-cyan-600',
    icon: Camera 
  },
};

// Default config for unknown tags
const DEFAULT_CONFIG = {
  color: 'bg-gray-500 text-white',
  hoverColor: 'hover:bg-gray-600',
  icon: null
};

const TagBadge = ({ 
  tag, 
  size = 'sm', 
  clickable = false, 
  onClick,
  showIcon = true,
  className = ''
}) => {
  const config = TAG_CONFIG[tag] || DEFAULT_CONFIG;
  const Icon = config.icon;
  
  // Size classes
  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0.5',
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };
  
  const iconSizes = {
    xs: 'w-2.5 h-2.5',
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  const baseClasses = `
    inline-flex items-center gap-1 rounded-full font-medium
    ${sizeClasses[size]}
    ${config.color}
    ${clickable ? `cursor-pointer ${config.hoverColor} transition-colors` : ''}
    ${config.pulse ? 'animate-pulse' : ''}
    ${className}
  `;

  const content = (
    <>
      {showIcon && Icon && <Icon className={iconSizes[size]} />}
      <span className="capitalize">{tag.replace('-', ' ')}</span>
    </>
  );

  if (clickable && onClick) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick(tag);
        }}
        className={baseClasses}
        data-testid={`tag-badge-${tag}`}
      >
        {content}
      </button>
    );
  }

  return (
    <span className={baseClasses} data-testid={`tag-badge-${tag}`}>
      {content}
    </span>
  );
};

// Component to display multiple tags
export const TagList = ({ tags = [], size = 'sm', clickable = false, onTagClick, className = '' }) => {
  if (!tags || tags.length === 0) return null;

  return (
    <div className={`flex flex-wrap gap-1 ${className}`} data-testid="tag-list">
      {tags.map(tag => (
        <TagBadge
          key={tag}
          tag={tag}
          size={size}
          clickable={clickable}
          onClick={onTagClick}
        />
      ))}
    </div>
  );
};

// Tag selector component for post creation
export const TagSelector = ({ selectedTags = [], onChange, availableTags = [] }) => {
  const tags = availableTags.length > 0 ? availableTags : Object.keys(TAG_CONFIG);

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      onChange(selectedTags.filter(t => t !== tag));
    } else {
      onChange([...selectedTags, tag]);
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-muted-foreground">Tags (optional)</label>
      <div className="flex flex-wrap gap-2" data-testid="tag-selector">
        {tags.map(tag => {
          const isSelected = selectedTags.includes(tag);
          const config = TAG_CONFIG[tag] || DEFAULT_CONFIG;
          
          return (
            <button
              key={tag}
              type="button"
              onClick={() => toggleTag(tag)}
              className={`
                inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                transition-all duration-200
                ${isSelected 
                  ? `${config.color} ring-2 ring-offset-2 ring-offset-background ring-current` 
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }
              `}
              data-testid={`tag-option-${tag}`}
            >
              {config.icon && <config.icon className="w-3 h-3" />}
              <span className="capitalize">{tag.replace('-', ' ')}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TagBadge;
